import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import RemoveIcon from '@mui/icons-material/Remove'
import { Box, IconButton, Paper, Stack, Typography } from '@mui/material'
import { useCallback } from 'react'

import useAccessTokenAndShopId from '../../../hooks/useAccessTokenAndShopId/useAccessTokenAndShopId'
import { useGetPartsMatrices } from '../../../hooks/useGetPartsMatrices/useGetPartsMatrices'
import {
  useDeleteCannedJobCustomPartMutation,
  useUpdateCannedJobCustomPartMutation
} from '../../../redux/api/cannedJobApi'
import { CannedJobCustomPartEntry, CounterSaleExpanded } from '../../../sdk'
import { formatCurrency } from '../../../utils'
import { CannedJobPartsDialogUI } from '../../CannedJobs/PartsDialogUI/CannedJobPartsDialogUI'
import { useAddCustomPartsDialog } from '../../CannedJobs/useAddCustomPartsDialog/useAddCustomPartsDialog'
import useModifiedProducts from './useModifiedProducts'

interface Props {
  counterSale: CounterSaleExpanded | undefined
  readOnly: boolean
}

const CounterSaleCartProduct = ({ counterSale, readOnly }: Props) => {
  const { accessToken } = useAccessTokenAndShopId()
  const { allPartsMatrices } = useGetPartsMatrices([])
  const { modifiedProducts } = useModifiedProducts({
    counterSale,
    partsMatrixEntries: allPartsMatrices
  })

  const [updateCustomParts] = useUpdateCannedJobCustomPartMutation()
  const [deleteCustomPart] = useDeleteCannedJobCustomPartMutation()
  const { setCustomProps: setAddCustomPartsProps, customPartsDialogProps } =
    useAddCustomPartsDialog({
      submitCallback: undefined,
      cannedJobId: undefined,
      counterSaleId: counterSale?.entry?.id
    })

  const removePartFromCart = useCallback(
    (cannedJobCustomPartEntry: CannedJobCustomPartEntry) => {
      deleteCustomPart({
        authorization: accessToken,
        customPart: cannedJobCustomPartEntry.id!,
        counterSale: cannedJobCustomPartEntry.info?.counterSale
      })
    },
    [accessToken, deleteCustomPart]
  )

  const updateQuantity = useCallback(
    (
      cannedJobCustomPartEntry: CannedJobCustomPartEntry,
      isIncrement: boolean
    ) => {
      updateCustomParts({
        authorization: accessToken,
        cannedJobCustomPartEntry: {
          id: cannedJobCustomPartEntry.id,
          info: {
            ...cannedJobCustomPartEntry.info,
            quantity:
              (cannedJobCustomPartEntry.info!.quantity || 0) +
              (isIncrement ? 1 : -1)
          }
        }
      })
    },
    [accessToken, updateCustomParts]
  )

  return (
    <>
      {customPartsDialogProps && (
        <CannedJobPartsDialogUI {...customPartsDialogProps} />
      )}
      <Stack
        direction="column"
        alignItems="center"
        justifyContent="center"
        spacing={2}>
        {(modifiedProducts || []).map((modifiedProduct) => (
          <Paper
            key={modifiedProduct.product.id}
            variant="outlined"
            sx={{ width: '100%' }}>
            <Box padding={2}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between">
                <Typography variant="h6">
                  <b>{modifiedProduct.product.info?.name}</b>
                </Typography>
                {!readOnly && (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <IconButton
                      onClick={() =>
                        setAddCustomPartsProps({
                          existingEntry: modifiedProduct.product
                        })
                      }>
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      onClick={() =>
                        removePartFromCart(modifiedProduct.product)
                      }>
                      <DeleteIcon />
                    </IconButton>
                  </Stack>
                )}
              </Stack>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between">
                <Stack direction="row" alignItems="center" spacing={1}>
                  {!readOnly && (
                    <IconButton
                      disabled={
                        (modifiedProduct.product.info?.quantity || 0) <= 1
                      }
                      onClick={() =>
                        updateQuantity(modifiedProduct.product, false)
                      }>
                      <RemoveIcon />
                    </IconButton>
                  )}
                  <Typography variant="h6">
                    {modifiedProduct.product.info?.quantity}
                  </Typography>
                  {!readOnly && (
                    <IconButton
                      onClick={() =>
                        updateQuantity(modifiedProduct.product, true)
                      }>
                      <AddIcon />
                    </IconButton>
                  )}
                </Stack>
                <Typography variant="caption">{`Unit. ${formatCurrency(
                  modifiedProduct.price,
                  true
                )}`}</Typography>
                <Typography variant="body2">
                  {formatCurrency(
                    modifiedProduct.price *
                      (modifiedProduct.product.info?.quantity || 0),
                    true
                  )}
                </Typography>
              </Stack>
            </Box>
          </Paper>
        ))}
      </Stack>
    </>
  )
}

export default CounterSaleCartProduct
